import { type FC, useState, useEffect, useRef } from "react";

import { FileText } from "lucide-react";

import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import { FieldError } from "@/shared/components/ui/FieldError";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { useUserContacts } from "@/shared/hooks/useUserContacts";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

const MARRIED_KEY = "admin_family_married_at_death";
const SPOUSE_FIRST_KEY = "admin_family_spouse_first_name";
const SPOUSE_MIDDLE_KEY = "admin_family_spouse_middle_name";
const SPOUSE_LAST_KEY = "admin_family_spouse_last_name";
const SPOUSE_STATUS_KEY = "admin_family_spouse_status";

export const AdminMarriagesScreen: FC<FlowCustomComponentProps> = ({
  answers,
  setAnswer,
  errors,
}) => {
  const { person } = useAppContext();
  const name = person?.first_name ?? "the decedent";

  const { contacts } = useUserContacts();
  const [seededFromCert, setSeededFromCert] = useState(false);
  const seededRef = useRef(false);

  const married = (answers[MARRIED_KEY] as string) ?? "";
  const spouseFirst = (answers[SPOUSE_FIRST_KEY] as string) ?? "";
  const spouseMiddle = (answers[SPOUSE_MIDDLE_KEY] as string) ?? "";
  const spouseLast = (answers[SPOUSE_LAST_KEY] as string) ?? "";
  const spouseStatus = (answers[SPOUSE_STATUS_KEY] as string) ?? "";

  useEffect(() => {
    if (seededRef.current || married || spouseFirst) return;

    const spouseContact =
      contacts.find((c) => c.extracted_from_document && c.relationship?.toLowerCase() === "spouse")
      ?? contacts.find((c) => c.relationship?.toLowerCase() === "spouse");

    if (spouseContact) {
      seededRef.current = true;
      setAnswer(MARRIED_KEY, "yes");
      if (spouseContact.first_name) setAnswer(SPOUSE_FIRST_KEY, spouseContact.first_name);
      if (spouseContact.middle_name) setAnswer(SPOUSE_MIDDLE_KEY, spouseContact.middle_name);
      if (spouseContact.last_name) setAnswer(SPOUSE_LAST_KEY, spouseContact.last_name);
      if (spouseContact.person_status) setAnswer(SPOUSE_STATUS_KEY, spouseContact.person_status);
      setSeededFromCert(!!spouseContact.extracted_from_document);
    }
  }, [contacts, married, spouseFirst, setAnswer]);

  const handleMarriedSelect = (value: string) => {
    setAnswer(MARRIED_KEY, value);
    if (value !== "yes") {
      setAnswer(SPOUSE_FIRST_KEY, "");
      setAnswer(SPOUSE_MIDDLE_KEY, "");
      setAnswer(SPOUSE_LAST_KEY, "");
      setAnswer(SPOUSE_STATUS_KEY, "");
    }
  };

  return (
    <div className="flex flex-col gap-8">
      {seededFromCert && (
        <div className="flex items-start gap-3 rounded-lg border border-brand bg-brand-subtle p-4">
          <FileText className="mt-0.5 size-5 shrink-0 text-brand" />
          <div className="flex flex-col gap-0.5">
            <p className="text-sm font-medium">
              We filled this in from {name}'s death certificate
            </p>
            <p className="text-sm text-secondary">
              Review each answer and confirm it's right, or make a change.
            </p>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <p className="font-sans text-xl font-semibold leading-[30px]">
            Was {name} married when they passed away?
          </p>
          <p className="text-sm text-secondary tracking-[-0.28px]">
            Marriage decides who the court places first in line to serve as administrator and
            inherit the estate. Getting this right up front shapes the exact forms your local
            court will require.
          </p>
        </div>

        <RadioGroup
          name="married_at_death"
          value={married}
          onValueChange={handleMarriedSelect}
          className="flex flex-col gap-3"
        >
          <OptionCard
            label={married === "yes" ? `Yes, ${name} was married` : "Yes"}
            selected={married === "yes"}
            onClick={() => handleMarriedSelect("yes")}
            variant="radio"
            value="yes"
          />
          <OptionCard
            label={`No, ${name} wasn't married`}
            selected={married === "no"}
            onClick={() => handleMarriedSelect("no")}
            variant="radio"
            value="no"
          />
        </RadioGroup>
        <FieldError message={errors?.married_at_death} />
      </div>

      {married === "yes" && (
        <div className="flex flex-col gap-4">
          <div className="border-t border-primary" />

          <p className="font-sans text-xl font-semibold leading-[30px]">
            Tell us about {name}'s spouse
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label>First name</Label>
              <Input
                placeholder="First name"
                value={spouseFirst}
                onChange={(e) => setAnswer(SPOUSE_FIRST_KEY, e.target.value)}
                className={errors?.spouse_first_name ? "border-destructive" : ""}
              />
              <FieldError message={errors?.spouse_first_name} />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Middle name <span className="text-tertiary font-normal">(optional)</span></Label>
              <Input
                placeholder="Middle name"
                value={spouseMiddle}
                onChange={(e) => setAnswer(SPOUSE_MIDDLE_KEY, e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Last name</Label>
            <Input
              placeholder="Last name"
              value={spouseLast}
              onChange={(e) => setAnswer(SPOUSE_LAST_KEY, e.target.value)}
              className={errors?.spouse_last_name ? "border-destructive" : ""}
            />
            <FieldError message={errors?.spouse_last_name} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Status</Label>
            <Select
              value={spouseStatus}
              onValueChange={(val) => setAnswer(SPOUSE_STATUS_KEY, val)}
            >
              <SelectTrigger className={errors?.spouse_status ? "border-destructive" : ""}>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="living">Living</SelectItem>
                <SelectItem value="passed_away">Passed away</SelectItem>
              </SelectContent>
            </Select>
            <FieldError message={errors?.spouse_status} />
          </div>
        </div>
      )}
    </div>
  );
};
